<template>
  <md-content class="iv-type-tab md-scrollbar scroll-content">
    <label v-if="centerAvailable">
      Center Locations          
    </label>
    <radio-buttons
      v-if="centerAvailable"
      bordered pill-shaped multiple
      active-color="blue-7"
      inactive-color="none"
      :options="centerOptions"
      :value="state.centerSites"
      :disabled="readonly"
      @input="sites => updateCenterSites(sites)"
    />
    <label>
      Left Locations
      <help-tooltip v-if="ivSiteError" type="error">
        {{ ivSiteError.message }}
      </help-tooltip>
      <help-tooltip v-if="typeWarning" type="warning">
        {{ typeWarning.message }}
      </help-tooltip>
      <help-tooltip v-if="leftError" type="error">
        {{ leftError.message }}
      </help-tooltip>
      <help-tooltip v-if="siteWarning" type="warning">
        {{ siteWarning.message }}
      </help-tooltip>
      <help-tooltip v-if="leftSideWarning" type="warning">
        {{ leftSideWarning.message }}
      </help-tooltip>
    </label>
    <radio-buttons
      bordered pill-shaped multiple
      active-color="blue-7"
      inactive-color="none"
      :options="leftRightOptions"
      :value="state.leftSites"
      :disabled="readonly"
      @input="sites => updateLeftSites(sites)"
    />
    <label>
      Right Locations
      <help-tooltip v-if="rightError" type="error">
        {{ rightError.message }}
      </help-tooltip>
      <help-tooltip v-if="rightSideWarning" type="warning">
        {{ rightSideWarning.message }}
      </help-tooltip>
    </label>
    <radio-buttons
      bordered pill-shaped multiple
      active-color="blue-7"
      inactive-color="none"
      :options="leftRightOptions"
      :value="state.rightSites"
      :disabled="readonly || state.matchSides"
      @input="sites => updateRightSites(sites)"
    />
    <label>Match sides</label>
    <md-switch
      class="ui-theme-3"
      :disabled="readonly"
      :value="state.matchSides"
      @change="updateMatchSides"
    />
    <template v-if="showGauges">
      <label>
        Needle Gauges
        <help-tooltip v-if="needleError" type="error">
          {{ needleError.message }}
        </help-tooltip>
        <help-tooltip v-if="needleGaugeWarning" type="warning">
          {{ needleGaugeWarning.message }}
        </help-tooltip>
      </label>
      <radio-buttons
        bordered pill-shaped multiple
        active-color="blue-7"
        inactive-color="none"
        :options="gaugeOptions"
        :value="state.gauges"
        :disabled="readonly"
        @input="gauges => updateGauges(gauges)"
      />
    </template>
    <template v-if="showSets">
      <label>
        Needle Sets
        <help-tooltip v-if="needleError" type="error">
          {{ needleError.message }}
        </help-tooltip>
        <help-tooltip v-if="needleSetWarning" type="warning">
          {{ needleSetWarning.message }}
        </help-tooltip>
      </label>
      <radio-buttons
        bordered pill-shaped multiple
        active-color="blue-7"
        inactive-color="none"
        :options="setOptions"
        :value="state.gauges"
        :disabled="readonly"
        @input="sets => updateSets(sets)"
      />
    </template>
    <label>
      Saline Lock
      <help-tooltip v-if="salineLockWarning" type="warning">
        {{ salineLockWarning.message }}
      </help-tooltip>
    </label>
    <toggle-switch
      :value="state.salineLock"
      :readonly="readonly"
      @toggle="updateSalineLock"
    />
    <template v-if="showPorts">
      <label>
        Allow two ports        
        <help-tooltip v-if="portWarning" type="warning">
          {{ portWarning.message }}
        </help-tooltip>
      </label>
      <md-switch
        class="ui-theme-3"
        :value="state.isTwoPorts"
        :disabled="readonly"
        @change="updateTwoPorts"
      />
    </template>
  </md-content>
</template>

<script>
import { get } from 'lodash';

import iv from '~lib/common/config/iv';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import RadioButtons from '~lib/common/components/RadioButtons';

import ToggleSwitch from '../../common/ToggleSwitch';

export default {
  name: 'iv-type-tab',
  components: {
    HelpTooltip,
    RadioButtons,
    ToggleSwitch,
  },
  props: {
    ivType: {
      type: Object,
      default: () => null
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showGauges: {
      type: Boolean,
      default: false,
    },
    showSets: {
      type: Boolean,
      default: false,
    },
    showPorts: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      state: this.getLocalState()
    };
  },
  computed: {
    label() {
      const category = iv.categories.find(c => c.key === this.ivType.type);
      return category ? category.label.en : 'Unknown';
    },
    centerAvailable() {
      return this.ivType.type !== 'PERIPHERAL' || this.state.centerSites.length
    },
    leftRightOptions() {
      return iv.sites
        .filter(site => site.category === this.ivType.type && !site.centerOnly)
        .map(site => ({
          label: site.label.en,
          value: site.key,
        }));
    },
    centerOptions() {
      return iv.sites
        .filter(site => site.category === this.ivType.type && site.centerOnly)
        .map(site => ({
          label: site.label.en,
          value: site.key,
        }));
    },
    gaugeOptions() {
      return iv.needleGauges
        .filter(gauge => gauge.category === this.ivType.type)
        .map(gauge => ({
          label: gauge.label.en,
          value: gauge.key
        }));
    },
    setOptions() {
      return iv.needleSets.map(set => ({
        label: set.label.en,
        value: set.key
      }));
    },
    ivSiteErrors() {
      return this.$store
        ? get(this.$store.getters['validation/errorsByFieldAndId'], 'iv.site')
        : [];
    },
    ivNeedleErrors() {
      return this.$store
        ? get(this.$store.getters['validation/errorsByFieldAndId'], 'iv.needle')
        : [];
    },
    ivSiteError() {
      return this.ivSiteErrors && this.ivSiteErrors.find(
        error => error.metadata.type === 'ANY'
      );
    },
    leftError() {
      return this.ivSiteErrors && this.ivSiteErrors.find(
        error => error.metadata.type === this.ivType.type &&  error.metadata.side === 'LEFT'
      );
    },
    rightError() {
      return this.ivSiteErrors && this.ivSiteErrors.find(
        error => error.metadata.type === this.ivType.type &&  error.metadata.side === 'RIGHT'
      );
    },
    needleError() {
      return this.ivNeedleErrors && this.ivNeedleErrors.find(
        error => error.metadata.type === this.ivType.type
      );
    },
    ivWarnings() {
      return this.$store
        ? get(this.$store.getters['validation/warningsByField'], 'iv')
        : [];
    },
    typeWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'type' && warning.metadata.type === this.ivType.type
      );
    },
    siteWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'site'
      );
    },
    leftSideWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'side' && warning.metadata.side === 'LEFT'
      );
    },
    rightSideWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'side' && warning.metadata.side === 'RIGHT'
      );
    },
    needleGaugeWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'needleGauge'
      );
    },
    needleSetWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'needleSet'
      );
    },
    salineLockWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'salineLock' && warning.metadata.type === this.ivType.type
      );
    },
    portWarning() {
      return this.ivWarnings && this.ivWarnings.find(
        warning => warning.id === 'port' && warning.metadata.type === this.ivType.type
      );
    },
  },
  methods: {
    getLocalState() {
      const localState = {
        type: this.ivType.type,
        leftSites: [],
        rightSites: [],
        centerSites: [],
        matchSides: false,
        salineLock: false,
        isTwoPorts: false,
      };

      for (const ivSite of this.ivType.sites) {
        for (const ivSide of ivSite.sides) {
          if (ivSide === 'LEFT') {
            localState.leftSites.push(ivSite.site);
          }
          if (ivSide === 'RIGHT') {
            localState.rightSites.push(ivSite.site);
          }
          if (ivSide === 'BOTH') {
            localState.centerSites.push(ivSite.site)
          }
        }
      }
      localState.matchSides = this.ivType.matchSides;
      if (this.showGauges) {
        localState.gauges = this.ivType.gauges;
      }
      if (this.showSets) {
        localState.gauges = this.ivType.gauges;
      }
      localState.salineLock = this.ivType.salineLock;
      localState.isTwoPorts = this.ivType.ports.includes('TWO_PORTS');

      return localState;
    },
    getUpdatedType() {
      const updatedType = {
        type: this.ivType.type
      };
      updatedType.sites = iv.sites
        .filter(ivSite => ivSite.category === this.ivType.type)
        .filter(ivSite => 
          this.state.leftSites.includes(ivSite.key) ||
          this.state.rightSites.includes(ivSite.key) ||
          this.state.centerSites.includes(ivSite.key)
        ).map(ivSite => {
          const sides = [];
          if (this.state.leftSites.includes(ivSite.key)) {
            sides.push('LEFT')
          }
          if (this.state.rightSites.includes(ivSite.key)) {
            sides.push('RIGHT')
          }
          if (this.state.centerSites.includes(ivSite.key)) {
            sides.push('BOTH')
          }
          return {
            site: ivSite.key,
            sides
          };
        });
      updatedType.matchSides = this.state.matchSides;
      updatedType.gauges = this.state.gauges;
      updatedType.salineLock = this.state.salineLock;
      updatedType.ports = this.state.isTwoPorts ? ['ONE_PORT', 'TWO_PORTS'] : ['ONE_PORT'];
      return updatedType;
    },
    syncSites() {
      if (this.state.matchSides) {
        this.state.rightSites = this.state.leftSites;  
      }
    },
    updateLeftSites(values) {
      this.state.leftSites = values;    
      this.syncSites();
      this.emitUpdate();
    },
    updateRightSites(values) {
      this.state.rightSites = values;
      this.syncSites();
      this.emitUpdate();
    },
    updateCenterSites(values) {
      this.state.centerSites = values;
      this.emitUpdate();
    },
    updateMatchSides(value) {
      this.state.matchSides = value;
      this.syncSites();
      this.emitUpdate();
    },
    updateGauges(gauges) {
      this.state.gauges = gauges;
      this.emitUpdate();
    },
    updateSets(sets) {
      this.state.gauges = sets;
      this.emitUpdate();
    },
    updateSalineLock(value) {
      this.state.salineLock = value;
      this.emitUpdate();
    },
    updateTwoPorts(value) {
      this.state.isTwoPorts = value;
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('updateType', this.getUpdatedType());
    },
    emitClick() {
      this.$emit('click', this.ivType.type);
    }
  }
}
</script>

<style scoped>
.iv-type-tab {
  display: grid;
  grid-template-rows: repeat(6, 1fr);
  grid-template-columns: auto 2fr;
  grid-gap: 1rem;
  align-items: center;
  flex-direction: row;
  padding-top: 1rem;
}

.toggle-switch {
  margin-bottom: 0 !important;
}

.md-switch {
  margin-top: 0;
  margin-bottom: 0;
}

@media (max-height: 768px) {
  .scroll-content {
    max-height: 250px;
    overflow-y: scroll;
  }
}
</style>